import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  basculerMaintenance, creerSauvegardeIntegrale, ecarterSentinelle, lancerNettoyage, lancerRestauration,
  leverVerrou, lireCouverture, lireInventaire, lireJournal, lireSauvegardes,
  surDemarrage, telechargerSauvegarde, chargerEtatDemarrage,
} from '../lib/demarrage';

const DATE_DEPART = '2026-11-02';
const fr = (valeur) => {
  if (!valeur) return '—';
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? String(valeur) : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' });
};
const ACTIONS = {
  'gel-des-ecritures': 'Gel des écritures', reouverture: 'Réouverture de l’exploitation',
  sauvegarde: 'Sauvegarde intégrale', telechargement: 'Téléchargement de l’archive',
  'nettoyage-debut': 'Nettoyage — début', 'nettoyage-termine': 'Nettoyage — terminé',
  'nettoyage-echec': 'Nettoyage — échec', 'nettoyage-non-conforme': 'Nettoyage — contrôles en échec',
  'restauration-debut': 'Retour arrière — début', 'restauration-terminee': 'Retour arrière — terminé',
  'restauration-echec': 'Retour arrière — échec', 'verrou-leve': 'Verrou levé', maintenance: 'Maintenance',
};
const champ = 'block w-full border border-slate-300 bg-white p-2 font-data text-sm';
const bouton = 'h-11 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] transition-colors duration-150 disabled:opacity-40';
const primaire = `${bouton} bg-slate-900 text-white hover:bg-slate-700`;
const danger = `${bouton} bg-red-700 text-white hover:bg-red-600`;
const neutre = `${bouton} border border-slate-300 bg-white text-slate-900 hover:bg-slate-100`;

const Bloc = ({ n, titre, children, testid }) => (
  <section data-testid={testid} className="border border-slate-200 bg-white">
    <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold tracking-tight text-slate-900">
      <span className="mr-2 inline-block bg-slate-900 px-2 py-0.5 font-data text-xs text-white">{n}</span>
      {titre}
    </p>
    <div className="space-y-3 px-4 py-4 text-sm text-slate-700">{children}</div>
  </section>
);

const Chiffres = ({ titre, valeurs, testid }) => (
  <div data-testid={testid}>
    <p className="mb-1 font-heading text-xs font-bold uppercase tracking-[0.08em] text-slate-500">{titre}</p>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-data text-xs sm:grid-cols-3 lg:grid-cols-4">
      {Object.entries(valeurs || {}).map(([k, v]) => (
        <p key={k} className="flex justify-between gap-2 border-b border-slate-100 py-0.5">
          <span className="text-slate-500">{k.replace(/_/g, ' ')}</span>
          <strong className={v ? 'text-slate-900' : 'text-slate-400'}>{String(v)}</strong>
        </p>
      ))}
    </div>
  </div>
);

export default function DemarrageZero() {
  const [etat, setEtat] = useState(null);
  const [couverture, setCouverture] = useState(null);
  const [inventaire, setInventaire] = useState(null);
  const [sauvegardes, setSauvegardes] = useState([]);
  const [journal, setJournal] = useState([]);
  const [simulation, setSimulation] = useState(null);
  const [rapport, setRapport] = useState(null);
  const [choix, setChoix] = useState('');
  const [phraseGel, setPhraseGel] = useState('');
  const [phraseNettoyage, setPhraseNettoyage] = useState('');
  const [phraseRestauration, setPhraseRestauration] = useState('');
  const [motifGel, setMotifGel] = useState('');
  const [motifVerrou, setMotifVerrou] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => surDemarrage(setEtat), []);

  const rafraichir = useCallback(async () => {
    const [c, s, j] = await Promise.all([lireCouverture(), lireSauvegardes(), lireJournal()]);
    setCouverture(c);
    setSauvegardes(s);
    setJournal(j);
    if (!choix && s.length) setChoix(s[0]._id);
    await chargerEtatDemarrage();
  }, [choix]);

  useEffect(() => {
    rafraichir().catch((e) => toast.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const agir = async (nom, action, succes) => {
    setBusy(nom);
    try {
      const r = await action();
      if (succes) toast.success(succes);
      await rafraichir();
      return r;
    } catch (e) {
      toast.error(e.message);
      return undefined;
    } finally {
      setBusy('');
    }
  };

  const ferme = etat?.maintenance || etat?.verrou;

  return (
    <div className="space-y-6" data-testid="demarrage-zero">
      <header className="border border-slate-200 bg-white px-4 py-4">
        <h1 className="font-heading text-xl font-bold tracking-tight text-slate-900">
          Démarrage zéro — remise à zéro contrôlée du {DATE_DEPART}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          L'opération se déroule dans la base d'exploitation elle-même. Contacts, personnel, utilisateurs,
          droits et réglages sont conservés ; les cuves sont supprimées et tous les historiques, stocks et
          soldes sont remis à zéro. Chaque étape se contrôle avant la suivante.
        </p>
        <p
          data-testid="demarrage-etat"
          className={`mt-3 inline-block px-3 py-1 font-data text-xs font-bold ${
            etat?.verrou ? 'bg-red-100 text-red-800' : ferme ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900'
          }`}
        >
          {etat?.verrou
            ? 'VERROU DE SÉCURITÉ : écritures bloquées jusqu’à récupération vérifiée'
            : ferme
              ? `MAINTENANCE ACTIVE depuis ${fr(etat?.depuis)} · étape ${etat?.etape}`
              : 'Exploitation normale — les saisies sont acceptées'}
        </p>
        {etat?.epoch && (
          <p className="mt-2 font-data text-xs text-slate-500" data-testid="demarrage-epoch">
            Jeu de données : {etat.epoch}
          </p>
        )}
      </header>

      <Bloc n="1" titre="Geler les écritures" testid="etape-maintenance">
        <p>
          Bloque toutes les saisies : postes, tâches planifiées, webhooks WhatsApp et Bourse, passerelle
          documentaire et notifications. La consultation reste possible. Les opérations déjà engagées sont
          abandonnées au moment de leur enregistrement.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="grow">
            <span className="text-xs text-slate-500">Confirmation</span>
            <input data-testid="phrase-gel" className={champ} value={phraseGel} placeholder="GELER LES ECRITURES"
              onChange={(e) => setPhraseGel(e.target.value)} />
          </label>
          <label className="grow">
            <span className="text-xs text-slate-500">Motif / fenêtre annoncée</span>
            <input data-testid="motif-gel" className={champ} value={motifGel} onChange={(e) => setMotifGel(e.target.value)} />
          </label>
          <button type="button" data-testid="activer-maintenance" disabled={busy || ferme} className={primaire}
            onClick={() => agir('gel', () => basculerMaintenance(true, phraseGel, motifGel), 'Écritures gelées.')}>
            Geler
          </button>
          <button type="button" data-testid="desactiver-maintenance" disabled={busy || !ferme} className={neutre}
            onClick={() => agir('degel', () => basculerMaintenance(false, '', ''), 'Exploitation rouverte.')}>
            Rouvrir l'exploitation
          </button>
        </div>
        {etat?.verrou && (
          <div className="border border-red-200 bg-red-50 p-3" data-testid="bloc-verrou">
            <p className="font-semibold text-red-800">
              Le verrou ne se lève qu'après une restauration vérifiée, avec motif écrit.
            </p>
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <input data-testid="motif-verrou" className={champ} style={{ maxWidth: 420 }} value={motifVerrou}
                placeholder="Comment la base a-t-elle été vérifiée ?" onChange={(e) => setMotifVerrou(e.target.value)} />
              <input data-testid="phrase-verrou" className={champ} style={{ maxWidth: 280 }} value={phraseRestauration}
                placeholder="RESTAURER LA SAUVEGARDE" onChange={(e) => setPhraseRestauration(e.target.value)} />
              <button type="button" data-testid="lever-verrou" disabled={busy} className={danger}
                onClick={() => agir('verrou', () => leverVerrou(phraseRestauration, motifVerrou), 'Verrou levé.')}>
                Lever le verrou
              </button>
            </div>
          </div>
        )}
      </Bloc>

      <Bloc n="2" titre="Vérifier la couverture du gel" testid="etape-couverture">
        <button type="button" data-testid="rafraichir-couverture" className={neutre} disabled={busy}
          onClick={() => agir('couverture', rafraichir)}>
          Actualiser les contrôles
        </button>
        {couverture && (
          <>
            <p data-testid="couverture-verdict" className={`inline-block px-3 py-1 font-data text-xs font-bold ${
              couverture.pret ? 'bg-emerald-100 text-emerald-900' : 'bg-red-100 text-red-800'}`}>
              {couverture.pret ? 'Couverture complète : nettoyage autorisé' : 'Nettoyage NON autorisé'}
            </p>
            {couverture.bloquants.map((b) => (
              <p key={b} className="text-red-700" data-testid="couverture-bloquant">• {b}</p>
            ))}
            {couverture.avertissements.map((a) => (
              <p key={a} className="text-amber-800" data-testid="couverture-avertissement">• {a}</p>
            ))}
            <div className="overflow-x-auto">
              <table className="w-full text-left font-data text-xs">
                <thead><tr className="text-slate-500">{['Service', 'Instance', 'État', 'Dernier signe de vie', ''].map((t) => <th key={t} className="py-1 pr-3">{t}</th>)}</tr></thead>
                <tbody data-testid="couverture-sentinelles">
                  {couverture.sentinelles.filter((s) => s.vivante).map((s) => (
                    <tr key={s.id} className="border-t border-slate-100">
                      <td className="py-1 pr-3">{s.nom}</td>
                      <td className="py-1 pr-3 text-slate-500">{s.instance}</td>
                      <td className={`py-1 pr-3 font-bold ${s.etat === 'non acquittée' ? 'text-red-700' : s.etat === 'gelée' ? 'text-emerald-700' : 'text-slate-700'}`}>{s.etat}</td>
                      <td className="py-1 pr-3 text-slate-500">{fr(s.vu_le)}</td>
                      <td className="py-1 pr-3">
                        {s.etat === 'non acquittée' && (
                          <button type="button" data-testid={`ecarter-${s.nom}`} disabled={busy} className="underline"
                            onClick={() => {
                              const motif = window.prompt('Comment avez-vous constaté que ce service est arrêté ?');
                              if (motif && motif.trim().length >= 5) agir('ecarter', () => ecarterSentinelle(s.id, motif.trim()), 'Instance déclarée arrêtée.');
                            }}>
                            déclarer arrêté
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="font-data text-xs text-slate-500" data-testid="couverture-remplacees">
              {couverture.sentinelles.filter((s) => !s.vivante).length} instance(s) remplacée(s) par un
              redémarrage, masquée(s) : elles ne tournent plus.
            </p>
            {couverture.operation_en_cours && (
              <p className="text-amber-800" data-testid="operation-en-cours">
                Opération « {couverture.operation_en_cours.operation} » en cours depuis
                {' '}{fr(couverture.operation_en_cours.debut)} ({couverture.operation_en_cours.par}) :
                aucune autre opération, aucune réouverture possible.
              </p>
            )}
            <p className="font-data text-xs text-slate-600" data-testid="couverture-refus">
              Saisies refusées pendant la fenêtre : {couverture.ecritures_refusees.maintenance} ·
              anciens postes : {couverture.ecritures_refusees.anciens_postes} ·
              sessions ouvertes : {couverture.sessions_ouvertes} ·
              dernière écriture métier : {fr(couverture.activite.derniere_ecriture_metier)}
            </p>
          </>
        )}
      </Bloc>

      <Bloc n="3" titre="Sauvegarde intégrale de la base réelle" testid="etape-sauvegarde">
        <p>
          Copie de toutes les collections, empreinte SHA256, relecture depuis l'espace privé de
          l'application. Téléchargez-en une copie hors ligne avant d'aller plus loin.
        </p>
        <div className="flex flex-wrap gap-3">
          <button type="button" data-testid="creer-sauvegarde" className={primaire} disabled={busy === 'sauvegarde' || !ferme}
            onClick={() => agir('sauvegarde', creerSauvegardeIntegrale, 'Sauvegarde créée, relue et vérifiée.')}>
            {busy === 'sauvegarde' ? 'Sauvegarde en cours…' : 'Créer une sauvegarde vérifiée'}
          </button>          <button type="button" data-testid="telecharger-sauvegarde" className={neutre} disabled={busy || !choix}
            onClick={() => agir('telechargement', () => telechargerSauvegarde(choix), 'Archive téléchargée.')}>
            Télécharger la sauvegarde choisie
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left font-data text-xs">
            <thead><tr className="text-slate-500">{['', 'Identifiant', 'Date', 'Documents', 'Taille', 'Empreinte', 'Restauration rejouée'].map((t) => <th key={t} className="py-1 pr-3">{t}</th>)}</tr></thead>
            <tbody data-testid="liste-sauvegardes">
              {sauvegardes.map((s) => (
                <tr key={s._id} className="border-t border-slate-100">
                  <td className="py-1 pr-3"><input type="radio" name="svg" checked={choix === s._id} onChange={() => setChoix(s._id)} /></td>
                  <td className="py-1 pr-3">{s._id}</td>
                  <td className="py-1 pr-3">{fr(s.cree_le)}</td>
                  <td className="py-1 pr-3">{s.documents}</td>
                  <td className="py-1 pr-3">{Math.round(s.octets / 1024)} Ko</td>
                  <td className="py-1 pr-3" title={s.sha256_archive}>{String(s.sha256_archive).slice(0, 12)}…</td>
                  <td className="py-1 pr-3">{s.restauration_testee ? 'oui' : 'non'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Bloc>

      <Bloc n="4" titre="Contrôles avant / après et simulation du nettoyage" testid="etape-simulation">
        <div className="flex flex-wrap gap-3">
          <button type="button" data-testid="lire-inventaire" className={neutre} disabled={busy}
            onClick={() => agir('inventaire', async () => setInventaire(await lireInventaire()))}>
            Relever l'état actuel
          </button>
          <button type="button" data-testid="simuler-nettoyage" className={neutre} disabled={busy}
            onClick={() => agir('simulation', async () => setSimulation(await lancerNettoyage({ simulation: true, date: DATE_DEPART, sauvegarde: choix })))}>
            Simuler le nettoyage (aucune modification)
          </button>
        </div>
        {inventaire && <Chiffres testid="inventaire-controles" titre={`État actuel — base ${inventaire.base}`} valeurs={inventaire.controles} />}
        {simulation && (
          <div className="space-y-2" data-testid="simulation-plan">
            <p className="font-semibold text-slate-900">
              {simulation.plan.documents_supprimes} document(s) seront supprimés dans
              {' '}{simulation.plan.collections_videes.length} registre(s) ; {simulation.plan.cuves_supprimees} cuve(s) supprimée(s).
            </p>
            <p className="font-data text-xs text-slate-600">
              Conservé : {simulation.plan.collections_conservees.join(', ')}
            </p>
            <p className="font-data text-xs text-slate-600">
              Classements comptables : {simulation.plan.classements_rattaches_supprimes} rattachés à une pièce
              supprimés, {simulation.plan.classements_regles_conservees} règle(s) de configuration conservée(s).
            </p>
            <p className="font-data text-xs text-slate-600">
              Clés remises à zéro : {simulation.plan.store_vide.join(', ') || '—'}
            </p>
            <p className="font-data text-xs text-red-700">
              Clés supprimées : {simulation.plan.store_supprime.join(', ') || '—'}
            </p>
          </div>
        )}
      </Bloc>

      <Bloc n="5" titre="Nettoyage réel" testid="etape-nettoyage">
        <p className="text-red-800">
          Irréversible sans la sauvegarde. Exige la maintenance active, une couverture complète, une
          sauvegarde vérifiée de moins de 12 h et 5 minutes sans aucune écriture. Tout échec laisse les
          écritures bloquées.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="grow">
            <span className="text-xs text-slate-500">Confirmation</span>
            <input data-testid="phrase-nettoyage" className={champ} value={phraseNettoyage} placeholder="REMISE A ZERO 2026-11-02"
              onChange={(e) => setPhraseNettoyage(e.target.value)} />
          </label>
          <button type="button" data-testid="lancer-nettoyage" className={danger}
            disabled={busy || !ferme || !choix || !couverture?.pret}
            onClick={() => agir('nettoyage', async () => setRapport(await lancerNettoyage({
              simulation: false, confirmation: phraseNettoyage, date: DATE_DEPART, sauvegarde: choix,
            })), 'Nettoyage terminé et contrôlé.')}>
            {busy === 'nettoyage' ? 'Nettoyage en cours…' : 'Lancer la remise à zéro'}
          </button>
        </div>
        {rapport && (
          <div className="space-y-3" data-testid="rapport-nettoyage">
            <Chiffres testid="rapport-avant" titre="Avant" valeurs={rapport.avant?.controles} />
            <Chiffres testid="rapport-apres" titre="Après" valeurs={rapport.apres?.controles} />
            <p className="text-slate-700">{rapport.suite}</p>
          </div>
        )}
      </Bloc>

      <Bloc n="6" titre="Retour arrière" testid="etape-restauration">
        <p>
          Remet la base dans l'état exact de la sauvegarde choisie, document par document, avec
          vérification des comptages. À utiliser dès qu'un contrôle échoue.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="grow">
            <span className="text-xs text-slate-500">Confirmation</span>
            <input data-testid="phrase-restauration" className={champ} value={phraseRestauration} placeholder="RESTAURER LA SAUVEGARDE"
              onChange={(e) => setPhraseRestauration(e.target.value)} />
          </label>
          <button type="button" data-testid="lancer-restauration" className={danger} disabled={busy || !ferme || !choix}
            onClick={() => agir('restauration', async () => setRapport(await lancerRestauration({
              confirmation: phraseRestauration, sauvegarde: choix,
            })), 'Base restaurée et vérifiée.')}>
            {busy === 'restauration' ? 'Restauration en cours…' : 'Restaurer cette sauvegarde'}
          </button>
        </div>
      </Bloc>

      <Bloc n="7" titre="Journal de l'opération" testid="etape-journal">
        <div className="overflow-x-auto">
          <table className="w-full text-left font-data text-xs">
            <thead><tr className="text-slate-500">{['Horodatage', 'Action', 'Par'].map((t) => <th key={t} className="py-1 pr-3">{t}</th>)}</tr></thead>
            <tbody data-testid="journal-demarrage">
              {journal.map((j) => (
                <tr key={j._id} className="border-t border-slate-100">
                  <td className="py-1 pr-3">{fr(j.le)}</td>
                  <td className="py-1 pr-3 font-semibold">{ACTIONS[j.action] || j.action}</td>
                  <td className="py-1 pr-3">{j.par}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Bloc>
    </div>
  );
}
